<template>
  <section 
    id="instagram-section" 
    class="instagram-section"
    :aria-label="$t('instagram.sectionLabel')"
  >
    <div class="instagram-section__container">
      <!-- Section Header -->
      <div class="instagram-section__header">
        <h2 class="instagram-section__title">
          {{ content.instagramTitle || $t('instagram.defaultTitle') }}
        </h2>
        <p 
          class="instagram-section__subtitle"
          v-if="content.instagramSubtitle"
        >
          {{ content.instagramSubtitle }}
        </p>
        
        <!-- Follow Button -->
        <div class="instagram-section__follow">
          <a
            :href="instagramUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="instagram-follow-button"
            :aria-label="`${$t('instagram.followUs')} ${content.instagramUsername || 'Instagram'}`"
          >
            <svg class="instagram-follow-button__icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            {{ $t('instagram.followUs') }}
          </a>
        </div>
      </div>

      <!-- Instagram Posts Grid -->
      <div class="instagram-section__grid">
        <div
          v-for="(post, index) in displayPosts"
          :key="`post-${index}`"
          class="instagram-post"
          :style="{ '--animation-delay': `${index * 0.1}s` }"
        >
          <a
            :href="post.url"
            target="_blank"
            rel="noopener noreferrer"
            class="instagram-post__link"
            :aria-label="`${$t('instagram.viewPost')} ${post.caption || ''}`"
          >
            <!-- Post Image -->
            <div class="instagram-post__image-container">
              <img
                :src="post.image"
                :alt="post.alt || post.caption || $t('instagram.postImage')"
                class="instagram-post__image"
                loading="lazy"
                @error="handleImageError"
              >
              
              <!-- Overlay -->
              <div class="instagram-post__overlay">
                <div class="instagram-post__stats">
                  <div class="instagram-post__stat">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                    </svg>
                    <span>{{ formatNumber(post.likes) }}</span>
                  </div>
                  <div class="instagram-post__stat">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    <span>{{ formatNumber(post.comments) }}</span>
                  </div>
                </div>
                
                <!-- Instagram Icon -->
                <div class="instagram-post__icon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
              </div>

              <!-- Post Type Indicator -->
              <div 
                class="instagram-post__type"
                v-if="post.type !== 'image'"
              >
                <svg v-if="post.type === 'video'" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                <svg v-else-if="post.type === 'carousel'" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11.5 0L8 13.5l2.5 3.01L14 12.5l4 5.5H8.5zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z"/>
                </svg>
              </div>
            </div>

            <!-- Post Caption -->
            <div class="instagram-post__caption" v-if="post.caption">
              <p>{{ truncateCaption(post.caption) }}</p>
            </div>
          </a>
        </div>
      </div>

      <!-- Load More Button -->
      <div class="instagram-section__footer">
        <button
          class="instagram-load-more"
          @click="loadMorePosts"
          v-if="hasMorePosts"
          :disabled="isLoading"
        >
          <span v-if="!isLoading">{{ $t('instagram.loadMore') }}</span>
          <span v-else>{{ $t('instagram.loading') }}</span>
        </button>
        
        <!-- View All Link -->
        <a
          :href="instagramUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="instagram-view-all"
        >
          {{ $t('instagram.viewAll') }}
        </a>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

interface InstagramPost {
  id: string
  url: string
  image: string
  caption?: string
  alt?: string
  likes: number
  comments: number
  type: 'image' | 'video' | 'carousel'
  timestamp: string
}

interface InstagramContent {
  instagramTitle?: string
  instagramSubtitle?: string
  instagramUsername?: string
  posts?: InstagramPost[]
}

// Props
interface Props {
  content: InstagramContent
}

const props = withDefaults(defineProps<Props>(), {
  content: () => ({})
})

// Composables
const { t } = useI18n()

// Reactive data
const isLoading = ref(false)
const postsToShow = ref(6)
const allPosts = ref<InstagramPost[]>([])

// Computed properties
const instagramUrl = computed(() => {
  const username = props.content.instagramUsername || 'trailsofhooves'
  return `https://instagram.com/${username}`
})

const displayPosts = computed(() => {
  return allPosts.value.slice(0, postsToShow.value)
})

const hasMorePosts = computed(() => {
  return postsToShow.value < allPosts.value.length
})

// Methods
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K'
  }
  return num.toString()
}

const truncateCaption = (caption: string, maxLength: number = 80): string => {
  if (caption.length <= maxLength) return caption
  return caption.substring(0, maxLength) + '...'
}

const loadMorePosts = () => {
  isLoading.value = true
  
  // Simulate loading delay
  setTimeout(() => {
    postsToShow.value = Math.min(postsToShow.value + 6, allPosts.value.length)
    isLoading.value = false
  }, 500)
}

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.src = 'https://picsum.photos/400/400?random=fallback'
}

const loadInstagramPosts = () => {
  // Generate mock Instagram posts for development
  const mockPosts: InstagramPost[] = [
    {
      id: '1',
      url: 'https://instagram.com/p/mock1',
      image: 'https://picsum.photos/400/400?random=10',
      caption: '🐎 Training session with Thunder! This beautiful horse is making incredible progress in dressage. #TrailsOfHooves #HorseTraining #Dressage',
      likes: 1247,
      comments: 89,
      type: 'image',
      timestamp: '2024-01-15T10:30:00Z'
    },
    {
      id: '2',
      url: 'https://instagram.com/p/mock2',
      image: 'https://picsum.photos/400/400?random=11',
      caption: '🌅 Morning ride through the countryside. Nothing beats the peaceful moments with our horses at sunrise! #MorningRide #HorseLife #Peaceful',
      likes: 2156,
      comments: 134,
      type: 'video',
      timestamp: '2024-01-14T08:15:00Z'
    },
    {
      id: '3',
      url: 'https://instagram.com/p/mock3',
      image: 'https://picsum.photos/400/400?random=12',
      caption: '🏆 Competition day! Our team performed amazingly at the regional championships. So proud of both horses and riders! #Competition #Champions #TeamWork',
      likes: 3421,
      comments: 287,
      type: 'carousel',
      timestamp: '2024-01-13T16:45:00Z'
    },
    {
      id: '4',
      url: 'https://instagram.com/p/mock4',
      image: 'https://picsum.photos/400/400?random=13',
      caption: '🥕 Feeding time! Our horses love their fresh carrots and apples. Proper nutrition is key to their health and happiness. #HorseCare #Nutrition #Feeding',
      likes: 892,
      comments: 56,
      type: 'image',
      timestamp: '2024-01-12T12:20:00Z'
    },
    {
      id: '5',
      url: 'https://instagram.com/p/mock5',
      image: 'https://picsum.photos/400/400?random=14',
      caption: '💆 Grooming session with Star. Regular grooming keeps our horses healthy and strengthens the bond between horse and caretaker. #Grooming #HorseCare #Bond',
      likes: 1543,
      comments: 78,
      type: 'video',
      timestamp: '2024-01-11T14:10:00Z'
    },
    {
      id: '6',
      url: 'https://instagram.com/p/mock6',
      image: 'https://picsum.photos/400/400?random=15',
      caption: '🌿 New foal in the stable! Meet Luna, our newest addition to the family. She\'s already showing great promise! #NewFoal #Luna #HorseFamily #Breeding',
      likes: 4782,
      comments: 456,
      type: 'carousel',
      timestamp: '2024-01-10T09:30:00Z'
    },
    {
      id: '7',
      url: 'https://instagram.com/p/mock7',
      image: 'https://picsum.photos/400/400?random=16',
      caption: '🎯 Jump training session! Working on technique and building confidence for upcoming show jumping events. #ShowJumping #Training #Confidence',
      likes: 1876,
      comments: 145,
      type: 'video',
      timestamp: '2024-01-09T15:25:00Z'
    },
    {
      id: '8',
      url: 'https://instagram.com/p/mock8',
      image: 'https://picsum.photos/400/400?random=17',
      caption: '🏠 Stable tour! Check out our newly renovated facilities with improved ventilation and comfort for our horses. #StableTour #Facilities #HorseComfort',
      likes: 2341,
      comments: 198,
      type: 'carousel',
      timestamp: '2024-01-08T11:40:00Z'
    },
    {
      id: '9',
      url: 'https://instagram.com/p/mock9',
      image: 'https://picsum.photos/400/400?random=18',
      caption: '👨‍⚕️ Veterinary check-up day! Regular health monitoring ensures our horses stay in peak condition. #VetCheck #HealthCare #Prevention',
      likes: 1234,
      comments: 89,
      type: 'image',
      timestamp: '2024-01-07T13:15:00Z'
    }
  ]

  // Use provided posts or fallback to mock data
  allPosts.value = props.content.posts || mockPosts
}

// Lifecycle
onMounted(() => {
  loadInstagramPosts()
})
</script>

<style lang="scss" scoped>
.instagram-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #fdfbf7 0%, #f8f6f0 100%);
  position: relative;

  @media (max-width: 768px) {
    padding: 80px 0;
  }

  &__container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  &__header {
    text-align: center;
    margin-bottom: 60px;

    @media (max-width: 768px) {
      margin-bottom: 40px;
    }
  }

  &__title {
    font-size: 3rem;
    font-weight: 800;
    color: #2c1810;
    margin-bottom: 24px;
    line-height: 1.1;
    position: relative;
    padding-bottom: 12px;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 4px;
      background: linear-gradient(90deg, #ff6b35, #ff8c42);
      border-radius: 2px;
    }

    @media (max-width: 768px) {
      font-size: 2.25rem;
      margin-bottom: 20px;
    }
  }

  &__subtitle {
    font-size: 1.25rem;
    color: #5d4e37;
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto 32px;

    @media (max-width: 768px) {
      font-size: 1.125rem;
      margin-bottom: 24px;
    }
  }

  &__follow {
    margin-top: 32px;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    margin-bottom: 60px;

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
      margin-bottom: 40px;
    }

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  }

  &__footer {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;

    @media (max-width: 768px) {
      gap: 20px;
    }
  }
}

// Instagram Follow Button
.instagram-follow-button {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: linear-gradient(45deg, #405de6, #5851db, #833ab4, #c13584, #e1306c, #fd1d1d);
  color: white;
  text-decoration: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 8px 24px rgba(225, 48, 108, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(225, 48, 108, 0.4);
  }

  &__icon {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 768px) {
    padding: 14px 24px;
    font-size: 0.9375rem;
    gap: 10px;

    &__icon {
      width: 20px;
      height: 20px;
    }
  }
}

// Instagram Post
.instagram-post {
  animation: fadeInUp 0.6s ease-out;
  animation-delay: var(--animation-delay);
  animation-fill-mode: both;

  &__link {
    display: block;
    text-decoration: none;
    color: inherit;
  }

  &__image-container {
    position: relative;
    aspect-ratio: 1;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 12px;
    background: #f0f0f0;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    color: white;
  }

  &__link:hover &__overlay {
    opacity: 1;
  }

  &__link:hover &__image {
    transform: scale(1.05);
  }

  &__stats {
    display: flex;
    gap: 24px;
    align-items: center;
    position: absolute;
    bottom: 16px;
    left: 16px;

    @media (max-width: 768px) {
      gap: 16px;
      bottom: 12px;
      left: 12px;
    }
  }

  &__stat {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    font-weight: 600;

    svg {
      width: 16px;
      height: 16px;
    }

    @media (max-width: 768px) {
      font-size: 0.8125rem;
      gap: 4px;

      svg {
        width: 14px;
        height: 14px;
      }
    }
  }

  &__icon {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 24px;
    height: 24px;

    @media (max-width: 768px) {
      top: 12px;
      right: 12px;
      width: 20px;
      height: 20px;
    }
  }

  &__type {
    position: absolute;
    top: 12px;
    left: 12px;
    width: 20px;
    height: 20px;
    color: white;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));

    @media (max-width: 768px) {
      top: 8px;
      left: 8px;
      width: 16px;
      height: 16px;
    }
  }

  &__caption {
    padding: 0 8px;

    p {
      font-size: 0.875rem;
      color: #5d4e37;
      line-height: 1.4;
      margin: 0;

      @media (max-width: 768px) {
        font-size: 0.8125rem;
      }
    }
  }
}

// Load More Button
.instagram-load-more {
  background: #ff6b35;
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);

  &:hover:not(:disabled) {
    background: #ff8c42;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 107, 53, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 14px 24px;
    font-size: 0.9375rem;
  }
}

// View All Link
.instagram-view-all {
  color: #5d4e37;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: color 0.3s ease;

  &:hover {
    color: #ff6b35;
  }

  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }
}

// Animations
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
